import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Search, MapPin, Navigation, Phone, Route, Clock, ChevronRight, X, ShieldCheck,
  SlidersHorizontal, List as ListIcon, Map as MapIcon, AlertCircle, PhoneCall,
  Info, ExternalLink, Flag, CircleDot, CalendarClock, Cross, Footprints, Video, Pill,
} from "lucide-react";

const DEFAULT_LOCATION = "La Londe-les-Maures";
const CATS = [
  ["UNSCHEDULED_CARE", "Soins non programmés"], ["MEDICAL_GUARD_CENTER", "Maison médicale de garde"],
  ["MEDICAL_CENTER", "Centre médical"], ["HOSPITAL_EMERGENCY", "Urgences hospitalières"],
  ["SOS_DOCTORS", "SOS Médecins"], ["PEDIATRIC_CARE", "Centre pédiatrique"],
  ["ON_CALL_PHARMACY", "Pharmacie de garde"],
];
const SORTS = [["default", "Pertinence"], ["distance", "Plus proche"], ["open", "Ouvert maintenant"], ["name", "Ordre alphabétique"]];
const EMPTY_FILTERS = { openNow: false, walkIn: false, appointment: false, maxKm: null, adults: false, children: false, radiology: false, lab: false, pmr: false };

const STATUS_BADGE = {
  OPEN: { label: "Ouvert maintenant", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  CLOSING_SOON: { label: "Ferme bientôt", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  CLOSED: { label: "Fermé", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  UNKNOWN: { label: "Horaires à vérifier", cls: "bg-slate-100 text-slate-500 border-slate-300" },
};

const StatusBadge = ({ status, testid }) => {
  const b = STATUS_BADGE[status] || STATUS_BADGE.UNKNOWN;
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${b.cls}`}><CircleDot className="h-3.5 w-3.5" /> {b.label}</span>;
};

const accessLabel = (c) =>
  c.callBeforeVisit ? "Appel préalable obligatoire"
  : c.accessType === "APPOINTMENT" || c.appointmentRequired ? "Réservation nécessaire"
  : c.accessType === "PHONE_REGULATION" ? "Régulation téléphonique (15)"
  : c.accessType === "WALK_IN" ? "Sans rendez-vous" : "Accès à confirmer";

export default function InstapharmCentres() {
  const navigate = useNavigate();
  const [query, setQuery] = useState(DEFAULT_LOCATION);
  const [coords, setCoords] = useState(null);
  const [category, setCategory] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("default");
  const [view, setView] = useState("list");
  const [showFilters, setShowFilters] = useState(false);
  const [centers, setCenters] = useState([]);
  const [origin, setOrigin] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [geoLoading, setGeoLoading] = useState(false);
  const [step, setStep] = useState("list"); // liste des centres — unique écran
  const [tc, setTc] = useState(null);
  const [pending, setPending] = useState(null); // provider téléconsult en attente de confirmation

  const fetchCenters = useCallback(async (opts = {}) => {
    setLoading(true);
    try {
      const p = { sort: opts.sort ?? sort };
      const c = opts.coords !== undefined ? opts.coords : coords;
      if (c) { p.lat = c.lat; p.lng = c.lng; } else { p.q = opts.query ?? query; }
      const cat = opts.category !== undefined ? opts.category : category;
      if (cat) p.category = cat;
      const f = opts.filters ?? filters;
      Object.entries({ openNow: f.openNow, walkIn: f.walkIn, appointment: f.appointment, adults: f.adults, children: f.children, radiology: f.radiology, lab: f.lab, pmr: f.pmr }).forEach(([k, v]) => { if (v) p[k] = true; });
      if (f.maxKm) p.maxKm = f.maxKm;
      const { data } = await api.get("/medical-centers", { params: p });
      setCenters(data.centers); setOrigin(data.origin);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [sort, coords, query, category, filters]);

  useEffect(() => { api.get("/instapharm/config").then((r) => setTc(r.data.teleconsultation)).catch(() => {}); }, []);

  // Arrivée directe sur la liste des centres (unique écran) — le bouton Retour ramène à l'accueil.
  useEffect(() => {
    fetchCenters({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enterList = (opts = {}) => { setStep("list"); fetchCenters(opts); };
  const pickProfile = (p) => {
    if (p === "cannot_move") { setStep("teleconsult"); return; }
    enterList({});
  };
  const entryGeo = () => {
    if (!("geolocation" in navigator)) { toast.error("Géolocalisation indisponible."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { const c = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setCoords(c); setQuery("Ma position"); setGeoLoading(false); enterList({ coords: c }); },
      () => { toast.error("Autorisez la localisation ou saisissez une ville."); setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  const confirmRedirect = () => { if (pending?.url) window.open(pending.url, "_blank", "noopener,noreferrer"); setPending(null); };

  const searchByText = () => { setCoords(null); fetchCenters({ coords: null }); };
  const searchAround = () => {
    if (!("geolocation" in navigator)) { toast.error("Géolocalisation indisponible."); return; }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { const c = { lat: pos.coords.latitude, lng: pos.coords.longitude }; setCoords(c); setQuery("Ma position"); fetchCenters({ coords: c }); setGeoLoading(false); },
      () => { toast.error("Autorisez la localisation ou saisissez une ville."); setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  const pickCategory = (v) => { const nv = category === v ? null : v; setCategory(nv); fetchCenters({ category: nv }); };
  const setF = (patch) => { const nf = { ...filters, ...patch }; setFilters(nf); fetchCenters({ filters: nf }); };
  const clearFilters = () => { setFilters(EMPTY_FILTERS); setCategory(null); fetchCenters({ filters: EMPTY_FILTERS, category: null }); };
  const changeSort = (s) => { setSort(s); fetchCenters({ sort: s }); };

  const activeFilterCount = Object.values(filters).filter((v) => v && v !== null).length;

  // Écran téléconsultation ("Sans se déplacer") — le Retour ramène à la liste des centres.
  if (step === "teleconsult") {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <div className="max-w-xl mx-auto px-4 py-6">
          <button onClick={() => setStep("list")} data-testid="centres-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronRight className="h-4 w-4 rotate-180" /> Retour</button>
          <TeleconsultView tc={tc} setPending={setPending} onSeePlaces={() => setStep("list")} />
        </div>
        <RedirectDialog pending={pending} setPending={setPending} onConfirm={confirmRedirect} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* En-tête */}
        <button onClick={() => navigate("/")} data-testid="centres-back" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"><ChevronRight className="h-4 w-4 rotate-180" /> Retour</button>
        <div className="flex items-start gap-3 mb-2">
          <div className="h-11 w-11 rounded-2xl bg-emerald-600 grid place-items-center text-white shrink-0"><Cross className="h-6 w-6" /></div>
          <div>
            <h1 className="font-['Outfit'] text-3xl font-bold tracking-tight">Trouver un centre de soins</h1>
            <p className="text-base text-slate-500">Consultez les structures de soins non programmés autour de vous.</p>
          </div>
        </div>

        {/* Sélecteur Sur place / Sans se déplacer */}
        <div className="flex rounded-2xl border-2 border-slate-200 overflow-hidden mb-4 bg-white" data-testid="mode-switch">
          <button data-testid="mode-onsite" onClick={() => setStep("list")} className="flex-1 h-12 text-sm font-bold bg-emerald-600 text-white inline-flex items-center justify-center gap-1.5"><MapPin className="h-4 w-4" /> Sur place</button>
          <button data-testid="mode-remote" onClick={() => setStep("teleconsult")} className="flex-1 h-12 text-sm font-bold bg-white text-slate-600 inline-flex items-center justify-center gap-1.5"><Video className="h-4 w-4" /> Sans se déplacer</button>
        </div>

        <div className="flex items-center gap-3 rounded-2xl bg-red-50 border-2 border-red-200 p-4 mb-5" data-testid="urgency-banner">
          <AlertCircle className="h-6 w-6 text-red-600 shrink-0" />
          <p className="text-sm text-red-800 flex-1 font-medium">En cas d'urgence vitale, appelez immédiatement le 15 ou le 112.</p>
          <a href="tel:15" data-testid="call-15" className="h-12 px-5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-bold grid place-items-center whitespace-nowrap active:scale-95 transition-transform"><span className="flex items-center gap-2"><PhoneCall className="h-5 w-5" /> Appeler le 15</span></a>
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input data-testid="centres-search" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchByText()} placeholder="Ville ou code postal" className="h-14 pl-12 rounded-xl text-base border-slate-200" />
            </div>
            <button data-testid="search-text-btn" onClick={searchByText} className="h-14 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold active:scale-[0.98] transition-transform">Rechercher</button>
          </div>
          <button data-testid="search-around-btn" onClick={searchAround} disabled={geoLoading} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800">
            <Navigation className="h-4 w-4" /> {geoLoading ? "Localisation…" : "Rechercher autour de moi"}
          </button>
        </div>

        {/* Catégories */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-3" data-testid="category-chips">
          {CATS.map(([v, label]) => (
            <button key={v} data-testid={`category-${v}`} onClick={() => pickCategory(v)}
              className={`shrink-0 h-11 px-4 rounded-full text-sm font-semibold border-2 transition-colors ${category === v ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"}`}>{label}</button>
          ))}
        </div>

        {/* Barre outils : filtres / tri / vue */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <button data-testid="toggle-filters" onClick={() => setShowFilters((s) => !s)} className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-white border-2 border-slate-200 text-sm font-semibold text-slate-700">
            <SlidersHorizontal className="h-4 w-4" /> Filtres {activeFilterCount > 0 && <span className="ml-1 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-emerald-600 text-white text-xs">{activeFilterCount}</span>}
          </button>
          <select data-testid="sort-select" value={sort} onChange={(e) => changeSort(e.target.value)} className="h-11 px-3 rounded-xl bg-white border-2 border-slate-200 text-sm font-semibold text-slate-700">
            {SORTS.map(([v, l]) => <option key={v} value={v}>Trier : {l}</option>)}
          </select>
          <div className="ml-auto flex rounded-xl border-2 border-slate-200 overflow-hidden">
            <button data-testid="view-list" onClick={() => setView("list")} className={`h-11 px-4 text-sm font-semibold inline-flex items-center gap-1.5 ${view === "list" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`}><ListIcon className="h-4 w-4" /> Liste</button>
            <button data-testid="view-map" onClick={() => setView("map")} className={`h-11 px-4 text-sm font-semibold inline-flex items-center gap-1.5 ${view === "map" ? "bg-emerald-600 text-white" : "bg-white text-slate-600"}`}><MapIcon className="h-4 w-4" /> Carte</button>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4" data-testid="filters-panel">
            <div className="flex flex-wrap gap-2">
              {[["openNow", "Ouvert maintenant"], ["walkIn", "Sans rendez-vous"], ["appointment", "Rendez-vous possible"], ["adults", "Adultes"], ["children", "Enfants"], ["radiology", "Radiologie"], ["lab", "Analyses"], ["pmr", "Accessible PMR"]].map(([k, label]) => (
                <button key={k} data-testid={`filter-${k}`} onClick={() => setF({ [k]: !filters[k] })} className={`h-10 px-3.5 rounded-full text-sm font-medium border-2 ${filters[k] ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>{label}</button>
              ))}
              {[10, 25, 50].map((km) => (
                <button key={km} data-testid={`filter-max-${km}`} onClick={() => setF({ maxKm: filters.maxKm === km ? null : km })} className={`h-10 px-3.5 rounded-full text-sm font-medium border-2 ${filters.maxKm === km ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200"}`}>Moins de {km} km</button>
              ))}
            </div>
            <button data-testid="clear-filters" onClick={clearFilters} className="mt-3 text-sm font-semibold text-slate-500 underline underline-offset-2">Effacer les filtres</button>
          </div>
        )}

        {/* Résultats */}
        {loading ? (
          <div className="text-center py-12 text-slate-500">Chargement…</div>
        ) : centers.length === 0 ? (
          <EmptyState onExpand={(km) => setF({ maxKm: km })} onClearOpen={() => setF({ openNow: false })} />
        ) : view === "map" ? (
          <MapView centers={centers} origin={origin} onOpen={setSelected} />
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="centres-list">
            {centers.map((c) => <CenterCard key={c.id} c={c} onOpen={setSelected} />)}
          </div>
        )}
      </div>

      <Drawer open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DrawerContent className="max-w-lg mx-auto max-h-[92vh]">
          {selected && <CenterDetail id={selected.id} coords={coords} onClose={() => setSelected(null)} />}
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function CenterCard({ c, onOpen }) {
  const primary =
    c.callBeforeVisit ? { label: "Appeler avant de venir", href: c.phone ? `tel:${c.phone.replace(/\s/g, "")}` : null }
    : (c.accessType === "APPOINTMENT" || c.appointmentRequired) && c.bookingUrl ? { label: "Voir les disponibilités", href: c.bookingUrl, ext: true }
    : { label: "Voir l'itinéraire", href: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.address || c.name)}`, ext: true };
  return (
    <div data-testid={`center-card-${c.id}`} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex flex-col">
      {c.sponsored && <span data-testid={`sponsored-${c.id}`} className="self-start mb-2 text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">Sponsorisé</span>}
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">{c.categoryLabel}</div>
          <h3 className="font-['Outfit'] font-bold text-lg leading-tight mt-0.5">{c.name}</h3>
        </div>
      </div>
      <div className="text-sm text-slate-500 flex items-center gap-1.5 mb-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.city}{c.distanceKm != null ? ` • ${c.distanceKm} km` : ""}</div>
      <div className="mb-2"><StatusBadge status={c.status} testid={`card-status-${c.id}`} /></div>
      <p className="text-sm text-slate-600 flex items-center gap-1.5"><Clock className="h-4 w-4 text-slate-400" /> {c.hasHours ? (c.todayHours ? `Aujourd'hui : ${c.todayHours}` : "Fermé aujourd'hui") : "Horaires à confirmer par téléphone"}</p>
      <p className="text-sm text-slate-600 mt-1">{accessLabel(c)}</p>
      {c.services?.length > 0 && <p className="text-sm text-slate-500 mt-1 line-clamp-1">Services : {c.services.join(" • ")}</p>}
      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Vérifié le {c.verifiedAt ? new Date(c.verifiedAt).toLocaleDateString("fr-FR") : "—"}{c.needsVerification ? " · à reconfirmer" : ""}</p>
      <div className="grid grid-cols-3 gap-2 mt-4">
        {c.phone ? <a data-testid={`card-call-${c.id}`} href={`tel:${c.phone.replace(/\s/g, "")}`} className="h-12 rounded-xl bg-emerald-600 text-white text-sm font-semibold grid place-items-center"><Phone className="h-4 w-4" /></a> : <span className="h-12 rounded-xl bg-slate-100 text-slate-300 grid place-items-center"><Phone className="h-4 w-4" /></span>}
        <a data-testid={`card-route-${c.id}`} href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.address || c.name)}`} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl border-2 border-slate-200 text-slate-700 grid place-items-center"><Route className="h-4 w-4" /></a>
        <button data-testid={`card-detail-${c.id}`} onClick={() => onOpen(c)} className="h-12 rounded-xl border-2 border-emerald-200 text-emerald-700 text-sm font-semibold grid place-items-center">Fiche</button>
      </div>
    </div>
  );
}

function MapView({ centers, origin, onOpen }) {
  const withCoords = centers.filter((c) => c.lat && c.lng);
  const center = origin || (withCoords[0] ? [withCoords[0].lat, withCoords[0].lng] : [43.14, 6.06]);
  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200" style={{ height: 480 }} data-testid="centres-map">
      <MapContainer center={Array.isArray(center) ? center : [center[0], center[1]]} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {withCoords.map((c) => (
          <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={10}
            pathOptions={{ color: c.status === "OPEN" ? "#059669" : c.status === "CLOSING_SOON" ? "#d97706" : "#64748b", fillColor: c.status === "OPEN" ? "#059669" : c.status === "CLOSING_SOON" ? "#d97706" : "#94a3b8", fillOpacity: 0.85 }}>
            <Popup>
              <div className="text-sm">
                <div className="font-bold">{c.name}</div>
                <div className="text-slate-500">{c.categoryLabel}{c.distanceKm != null ? ` • ${c.distanceKm} km` : ""}</div>
                <button onClick={() => onOpen(c)} className="mt-1 text-emerald-700 font-semibold underline">Voir la fiche</button>
              </div>
            </Popup>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}

function EmptyState({ onExpand, onClearOpen }) {
  return (
    <div className="text-center py-12" data-testid="empty-state">
      <div className="h-14 w-14 rounded-full bg-slate-100 grid place-items-center mx-auto mb-3 text-slate-400"><Search className="h-7 w-7" /></div>
      <p className="text-base font-medium text-slate-700 mb-4">Aucun centre correspondant n'a été trouvé dans cette zone.</p>
      <div className="flex flex-wrap justify-center gap-2">
        <button data-testid="expand-25" onClick={() => onExpand(25)} className="h-11 px-4 rounded-xl border-2 border-slate-200 text-sm font-semibold">Élargir à 25 km</button>
        <button data-testid="expand-50" onClick={() => onExpand(50)} className="h-11 px-4 rounded-xl border-2 border-slate-200 text-sm font-semibold">Élargir à 50 km</button>
        <button data-testid="clear-open" onClick={onClearOpen} className="h-11 px-4 rounded-xl border-2 border-slate-200 text-sm font-semibold">Retirer « Ouvert maintenant »</button>
        <a href="tel:15" className="h-11 px-4 rounded-xl bg-red-600 text-white text-sm font-semibold grid place-items-center">Appeler le 15</a>
      </div>
    </div>
  );
}

function CenterDetail({ id, coords, onClose }) {
  const [c, setC] = useState(null);
  const [showHours, setShowHours] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMsg, setReportMsg] = useState("");
  useEffect(() => {
    const p = {}; if (coords) { p.lat = coords.lat; p.lng = coords.lng; }
    api.get(`/medical-centers/${id}`, { params: p }).then((r) => setC(r.data)).catch(() => {});
  }, [id, coords]);
  if (!c) return <div className="p-6 text-slate-500">Chargement…</div>;
  const digits = (c.phone || "").replace(/\s/g, "");
  const DAYS = [["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"], ["thursday", "Jeudi"], ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"]];
  const submitReport = async () => {
    if (!reportMsg.trim()) { toast.error("Décrivez l'erreur."); return; }
    try { await api.post(`/medical-centers/${id}/report`, { message: reportMsg }); toast.success("Merci, votre signalement a été transmis."); setReportOpen(false); setReportMsg(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const Row = ({ label, children }) => children ? <div className="py-2 border-b border-slate-100 last:border-0"><div className="text-xs text-slate-400">{label}</div><div className="text-sm text-slate-800">{children}</div></div> : null;
  return (
    <div className="overflow-y-auto px-5 pt-2 pb-8" data-testid="center-detail">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          {c.sponsored && <span className="text-[10px] font-bold uppercase bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5">Sponsorisé</span>}
          <div className="text-[11px] font-bold uppercase tracking-wide text-emerald-700 mt-1">{c.categoryLabel}</div>
          <h2 className="font-['Outfit'] text-2xl font-bold leading-tight mt-0.5">{c.name}</h2>
        </div>
        <button data-testid="detail-close" onClick={onClose} className="h-9 w-9 grid place-items-center rounded-full bg-slate-100 text-slate-500 shrink-0"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <StatusBadge status={c.status} testid="detail-status" />
        <span className="text-xs font-medium text-slate-600">{accessLabel(c)}</span>
      </div>
      <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3 mb-3" data-testid="detail-wait">{c.waitingTimeText}{c.waitingTimeUpdatedAt ? ` — mis à jour ${new Date(c.waitingTimeUpdatedAt).toLocaleString("fr-FR")} (${c.waitingTimeSource})` : ""}</p>

      <div className="bg-white rounded-2xl border border-slate-200 px-4 mb-4">
        <Row label="Adresse">{c.address}</Row>
        <Row label="Téléphone">{c.phone}</Row>
        {c.distanceKm != null && <Row label="Distance">{c.distanceKm} km</Row>}
        <Row label="Aujourd'hui">{c.hasHours ? (c.todayHours || "Fermé") : "À confirmer par téléphone"}</Row>
        <Row label="Demain">{c.tomorrowHours}</Row>
        <Row label="Public accueilli">{[c.adultsAccepted ? "Adultes" : null, c.childrenAccepted ? "Enfants" : null].filter(Boolean).join(" · ") || null}</Row>
        <Row label="Soins proposés">{c.services?.length ? c.services.join(" · ") : null}</Row>
        <Row label="Équipements">{c.equipment?.length ? c.equipment.join(" · ") : null}</Row>
        <Row label="Accessibilité PMR">{c.pmrAccessible == null ? null : (c.pmrAccessible ? "Oui" : "Non")}</Row>
        <Row label="Stationnement">{c.parking}</Row>
        <Row label="Moyens de paiement">{c.paymentMethods?.length ? c.paymentMethods.join(" · ") : null}</Row>
        <Row label="Documents à prévoir">{c.documentsToBring?.length ? c.documentsToBring.join(" · ") : null}</Row>
        <Row label="Source des informations">{c.informationSource}</Row>
      </div>

      {c.hasHours && (
        <>
          <button data-testid="toggle-all-hours" onClick={() => setShowHours((s) => !s)} className="text-sm font-semibold text-emerald-700 mb-2 inline-flex items-center gap-1"><CalendarClock className="h-4 w-4" /> {showHours ? "Masquer les horaires" : "Voir tous les horaires"}</button>
          {showHours && (
            <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4 text-sm" data-testid="all-hours">
              {DAYS.map(([k, l]) => {
                const slots = (c.openingHours || {})[k] || [];
                return <div key={k} className="flex justify-between py-1"><span className="text-slate-500">{l}</span><span className="font-medium">{slots.length ? slots.map((s) => `${s[0].replace(":", "h")}–${s[1].replace(":", "h")}`).join(", ") : "Fermé"}</span></div>;
              })}
            </div>
          )}
        </>
      )}

      {/* Avant de vous déplacer */}
      <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 mb-4" data-testid="before-you-go">
        <div className="flex items-center gap-2 text-blue-800 font-semibold text-sm mb-2"><Info className="h-4 w-4" /> Avant de vous déplacer</div>
        <ul className="text-sm text-blue-900/80 space-y-1 list-disc pl-5">
          <li>Appelez la structure si les horaires ne sont pas confirmés.</li>
          <li>Certaines structures demandent une régulation préalable par le 15.</li>
          <li>La prise en charge dépend de l'état du patient et des capacités du centre.</li>
          <li className="marker:text-red-600 text-red-600 font-semibold">En cas d'urgence vitale, appelez le 15 ou le 112.</li>
        </ul>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {digits ? <a data-testid="detail-call" href={`tel:${digits}`} className="h-14 rounded-xl bg-emerald-600 text-white font-semibold grid place-items-center"><span className="flex items-center gap-2"><Phone className="h-5 w-5" /> Appeler</span></a>
          : <span className="h-14 rounded-xl bg-slate-100 text-slate-400 font-medium grid place-items-center text-sm">Appel indisponible</span>}
        <a data-testid="detail-route" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(c.address || c.name)}`} target="_blank" rel="noopener noreferrer" className="h-14 rounded-xl border-2 border-emerald-600 text-emerald-700 font-semibold grid place-items-center"><span className="flex items-center gap-2"><Route className="h-5 w-5" /> Itinéraire</span></a>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-3">
        {c.websiteUrl && <a data-testid="detail-website" href={c.websiteUrl} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium grid place-items-center"><span className="flex items-center gap-1.5"><ExternalLink className="h-4 w-4" /> Site officiel</span></a>}
        {c.bookingUrl && <a data-testid="detail-booking" href={c.bookingUrl} target="_blank" rel="noopener noreferrer" className="h-12 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium grid place-items-center"><span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4" /> Voir les disponibilités</span></a>}
      </div>
      <button data-testid="detail-report" onClick={() => setReportOpen((s) => !s)} className="mt-4 inline-flex items-center gap-1.5 text-sm text-slate-500 underline underline-offset-2"><Flag className="h-4 w-4" /> Signaler une information incorrecte</button>
      {reportOpen && (
        <div className="mt-3 bg-white rounded-2xl border border-slate-200 p-4" data-testid="report-form">
          <Textarea data-testid="report-message" value={reportMsg} onChange={(e) => setReportMsg(e.target.value)} placeholder="Ex : le numéro de téléphone n'est plus valide…" rows={4} className="rounded-xl" />
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => { setReportOpen(false); setReportMsg(""); }} className="h-11 px-4 rounded-xl border border-slate-200 text-sm font-semibold">Annuler</button>
            <button data-testid="report-submit" onClick={submitReport} className="h-11 px-4 rounded-xl bg-emerald-600 text-white text-sm font-semibold">Envoyer</button>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400 mt-3 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Informations vérifiées le {c.verifiedAt ? new Date(c.verifiedAt).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—"}</p>
    </div>
  );
}

function TeleconsultView({ tc, setPending, onSeePlaces }) {
  return (
    <div data-testid="teleconsult-view">
      <div className="flex items-start gap-3 mb-4">
        <div className="h-11 w-11 rounded-2xl bg-[#1E3A8A] grid place-items-center text-white shrink-0"><Video className="h-6 w-6" /></div>
        <div>
          <h1 className="font-['Outfit'] text-2xl font-bold tracking-tight">Consulter rapidement un médecin</h1>
          <p className="text-sm text-slate-500">{tc?.subtitle || "Médecins généralistes en téléconsultation. Choisissez librement votre plateforme selon vos besoins et les disponibilités proposées."}</p>
        </div>
      </div>
      <div className="flex rounded-2xl border-2 border-slate-200 overflow-hidden mb-4 bg-white">
        <button data-testid="tc-mode-onsite" onClick={onSeePlaces} className="flex-1 h-12 text-sm font-bold bg-white text-slate-600 inline-flex items-center justify-center gap-1.5"><MapPin className="h-4 w-4" /> Sur place</button>
        <button className="flex-1 h-12 text-sm font-bold bg-emerald-600 text-white inline-flex items-center justify-center gap-1.5"><Video className="h-4 w-4" /> Sans se déplacer</button>
      </div>
      <div className="flex items-start gap-2 rounded-2xl bg-blue-50 border border-blue-200 p-4 mb-4" data-testid="tc-free-choice">
        <ShieldCheck className="h-5 w-5 text-blue-700 shrink-0 mt-0.5" />
        <p className="text-sm text-blue-900/90 leading-relaxed">Choisissez librement votre plateforme de téléconsultation. Pour préserver votre confidentialité, effectuez votre réservation sur votre téléphone ou demandez l'aide de notre équipe.</p>
      </div>
      <div className="space-y-3">
        {(tc?.providers || []).map((p) => (
          <button key={p.slug} data-testid={`tc-provider-${p.slug}`} onClick={() => setPending(p)}
            className="w-full text-left block bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-5 active:scale-[0.98]">
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-['Outfit'] font-bold text-slate-900">{p.name}</span>
              <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E3A8A]">{p.cta} <ExternalLink className="h-4 w-4" /></span>
            </div>
            <p className="text-sm text-slate-500">{p.note}</p>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-4">{tc?.notice}</p>
      <div className="flex items-start gap-2 rounded-2xl bg-slate-100 border border-slate-200 p-4 mt-4" data-testid="tc-counter-help">
        <Info className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-600 leading-relaxed">Besoin d'aide pour organiser votre téléconsultation ? Notre équipe est à votre disposition pour vous accompagner dans vos démarches, en toute confidentialité. Adressez-vous à notre équipe au comptoir.</p>
      </div>
    </div>
  );
}

function RedirectDialog({ pending, setPending, onConfirm }) {
  return (
    <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="redirect-notice-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2"><ExternalLink className="h-5 w-5 text-[#1E3A8A]" /> Vous allez quitter le site EYMI</DialogTitle>
          <DialogDescription className="text-left leading-relaxed pt-1">
            {pending?.name || "Cette plateforme"} est un service indépendant d'EYMI, ni affilié ni partenaire, sauf mention contraire résultant d'un accord écrit.
            Les disponibilités, comptes utilisateurs et réservations sont entièrement gérés par la plateforme externe.
            En continuant, vous quittez le site EYMI et serez redirigé vers {pending?.name || "la plateforme"}.
            Aucune donnée personnelle ou médicale saisie sur cette borne n'est transmise lors de la redirection.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-1">
          <button data-testid="redirect-cancel" onClick={() => setPending(null)} className="h-12 rounded-xl border border-slate-300 text-slate-700 font-medium">Annuler</button>
          <button data-testid="redirect-confirm" onClick={onConfirm} className="h-12 rounded-xl bg-[#1E3A8A] text-white font-medium inline-flex items-center justify-center gap-2">Continuer <ExternalLink className="h-4 w-4" /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

